import React, { useEffect, useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { AppContext } from 'src/providers/AppContextProvider';
import { IAppContextState } from 'src/reducers/AppContext';
import './PersonnelDialogStyle.scss';
import PersonnelHiringHistory from 'src/models/personnel/PersonnelHiringHistory';
import useFetch from 'use-http';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
// import moment from 'moment';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import PersonnelToast from '../components/PersonnelToast';
import TSDILandman from 'src/models/personnel/TSDILandman';

interface IAddPersonnelDialogProps {
    dialogName: string;
    selectedPersonnel: TSDILandman;
    personnelHiringHistory: Array<PersonnelHiringHistory>;
    refreshHiringHistory: () => void;
    hideDialog: () => void;
}

const PersonnelHiringHistoryDialog = (props: IAddPersonnelDialogProps) => {
    const { handleSubmit, register, reset, getValues, setValue } = useForm();
    const { get, post, put, delete: remove, response } = useFetch();
    // const [personnelHiringHistory, setPersonnelHiringHistory] = useState<Array<PersonnelHiringHistory>>();
    const [toast, setToast] = useState<{ message: string; color?: string, persistent?: boolean; } | null>(null);

    const appContext = useContext<IAppContextState>(AppContext);
    const currentUser = {
        name: appContext.currentUser.username,
        id: appContext.currentUser.userId,
        lmsNumber: appContext.currentUser.lmsNumber
    };

    // Initial render
    useEffect(() => {
        onCreate();

        return () => {
            onDestroy();
        };
    }, []);

    useEffect(() => {
        if (props.personnelHiringHistory?.length > 0) {
            reset({ history: props.personnelHiringHistory });
        }
    }, [props.personnelHiringHistory]);

    const onCreate = () => {
        console.log('PersonnelHiringHistoryDialog onCreate()');
    };

    const onDestroy = () => {
        console.log('PersonnelHiringHistoryDialog onDestroy()');
    };

    const showToast = (message: string, color?: string, persistent?: boolean) => {
        setToast({ message, persistent, color  });
    };

    // new row will default to hire or term based on last entry
    const determineNewStatus = () => {
        if (props.personnelHiringHistory?.length > 0) {
            let lastStatus = props.personnelHiringHistory[props.personnelHiringHistory.length - 1].tranType;
            if (lastStatus === 'Hired') {
                return 'Termed';
            } else if (lastStatus === 'Termed') {
                return 'Hired';
            } else {
                console.log('Bad transaction status in database: ', props.personnelHiringHistory[props.personnelHiringHistory.length - 1]);
                showToast('Bad transaction status in database: ' + props.personnelHiringHistory[props.personnelHiringHistory.length - 1], `rgba(255,0,0,0.5)`);
                return 'Unknown';
            }
        } else {
            return 'Hired';
        }
    };

    // auto fill name if employee number is entered
    const getByLMSNumber = async (context: string) => {
        let lmsNumber = getValues(`${context}No`);
        if (lmsNumber.length >= 3) {
            let result = await get(`/v1.0/landmen/personnel/${lmsNumber}/get-name-by-lms`);
            if (response.ok) {
                if (result !== '') {
                    setValue(`${context}`, result);
                    setValue(`${context}No`, lmsNumber);
                } else {
                    showToast('Unable to find valid name with provided lms number: ' + lmsNumber, `rgba(255,0,0,0.5)`);
                }
            } else {
                showToast('Unable to find user with provided lms number: ' + lmsNumber, `rgba(255,0,0,0.5)`);
            }
        }
    };

    const handleUpdateRow = async (index: number) => {
        let existingData = getValues();
        let commentRowData = { ...existingData.history[index], id: props.personnelHiringHistory[index].id };

        // converting data for proper db storage
        commentRowData.lmNo = props.selectedPersonnel.lmNo;
        commentRowData.tranDate = commentRowData.tranDate ? commentRowData.tranDate : new Date();
        commentRowData.tranType = commentRowData.tranType ? (commentRowData.tranType === 'Hired' ? 'H' : (commentRowData.tranType === 'Termed' ? 'T' : 'n/a')) : 'n/a';
        commentRowData.hireByNo = parseInt(commentRowData.hireByNo.toString(), 10);
        commentRowData.tranByNo = parseInt(currentUser.lmsNumber?.toString(), 10);
        commentRowData.tranBy = currentUser.name;
        commentRowData.timestamp = new Date().toISOString();

        let result = await put(`/v1.0/landmen/personnel/update-hire-history`, commentRowData);

        if (response.ok) {
            showToast('Hiring history updated successfully');
            props.refreshHiringHistory();
        } else {
            console.log('error with post: ', response);
            showToast('Unable to update hiring history: ' + result.message, `rgba(255,0,0,0.5)`);
        }
    };

    const handleDeleteRow = async (index: number) => {
        let existingData = getValues();
        let commentRowData = { ...existingData.history[index], id: props.personnelHiringHistory[index].id };

        let result = await remove(`/v1.0/landmen/personnel/${commentRowData.id}/delete-hire-history`);

        if (response.ok) {
            showToast('Hiring history deleted successfully');
            props.refreshHiringHistory();
        } else {
            console.log('error with delete: ', response);
            showToast('Unable to delete hiring history: ' + result.message, `rgba(255,0,0,0.5)`);
        }
    };

    const onSubmit = async (data: PersonnelHiringHistory) => {
        // converting data for proper db storage
        data.lmNo = props.selectedPersonnel.lmNo;
        data.hireByNo = parseInt(data.hireByNo.toString(), 10);
        data.tranByNo = data.tranByNo ? parseInt(data.tranByNo?.toString(), 10) : currentUser.lmsNumber;
        data.tranDate = data.tranDate ? data.tranDate : new Date();
        data.timestamp = new Date().toISOString();
        data.tranType = data.tranType ? (data.tranType === 'Hired' ? 'H' : (data.tranType === 'Termed' ? 'T' : 'n/a')) : 'n/a'; // default n/a to indicate something went wrong

        let result = await post(`/v1.0/landmen/personnel/create-hire-history`, data);
        if (response.ok) {
            showToast('Hiring history created successfully');
            props.refreshHiringHistory();
        } else {
            console.log('error with post: ', response);
            showToast('Unable to create hiring history: ' + result.message, `rgba(255,0,0,0.5)`);
        }
    };

    // new row component
    const NewHistoryEntry = () => {
        return (
            <tr style={{ width: '100%', gap: '1rem', marginBottom: '1rem' }}>
                <td style={{ width: '8%' }}>
                    <RHFSelect
                        register={register}
                        name="tranType"
                        style={{ width: '100%', display: 'block' }}
                        options={[
                            { value: 'Hired', label: 'Hired' },
                            { value: 'Termed', label: 'Termed' }
                        ]}
                        defaultVal={determineNewStatus()}
                    />
                </td>
                <td style={{ width: '7%' }}>
                    <RHFInputBox
                        type="date"
                        register={register}
                        name="tranDate"
                        style={{ width: '100%', display: 'block' }}
                    />
                </td>
                <td style={{ width: '5%' }}>
                    <RHFInputBox
                        type="text"
                        register={register}
                        name="hireByNo"
                        style={{ width: '100%', display: 'block' }}
                        onChange={() => getByLMSNumber('hireBy')}
                    />
                </td>
                <td style={{ width: '10%' }}>
                    <RHFInputBox
                        type="text"
                        register={register}
                        name="hireBy"
                        style={{ width: '100%', display: 'block' }}
                    />
                </td>
                <td style={{ width: '5%' }}>
                    <RHFInputBox
                        type="text"
                        register={register}
                        name="tranByNo"
                        style={{ width: '100%', display: 'block' }}
                        disabled={true}
                        defaultVal={currentUser.lmsNumber?.toString()}
                        onChange={() => getByLMSNumber('tranBy')}
                    />
                </td>
                <td style={{ width: '10%' }}>
                    <RHFInputBox
                        type="text"
                        register={register}
                        name="tranBy"
                        style={{ width: '100%', display: 'block' }}
                        disabled={true}
                        defaultVal={currentUser.name}
                    />
                </td>
                <td style={{ width: '13%' }}>
                    <RHFInputBox
                        type="text"
                        register={register}
                        name="timestamp"
                        style={{ width: '100%', display: 'block' }}
                        disabled={true}
                        defaultVal={new Date().toISOString()}
                    />
                </td>
                <td style={{ width: '32%' }}>
                    <RHFInputBox
                        type="text"
                        register={register}
                        name="notes"
                        style={{ width: '100%', display: 'block' }}
                    />
                </td>
            </tr>
        );
    };

    return (
        <Dialog title="Hiring History" dialogName={props.dialogName} bodyStyle={{ width: '75%', height: 'auto' }}>
            <div>
                {toast && (
                    <PersonnelToast
                        message={toast.message}
                        color={toast.color}
                        onClose={() => setToast(null)}
                        persistent={toast.persistent}
                    />
                )}
            </div>
            <div>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <table style={{ width: '100%',  borderSpacing: '15px' }} id="hireTable">
                        <thead>
                            <tr style={{ width: '100%' }}>
                                <th style={{ width: '8%' }}>Hire/Term</th>
                                <th style={{ width: '7%' }}>Hire/Term Date</th>
                                <th style={{ width: '5%' }}>Id</th>
                                <th style={{ width: '10%' }}>Hire/Termed By</th>
                                <th style={{ width: '5%' }}>Id</th>
                                <th style={{ width: '10%' }}>Last Entered By</th>
                                <th style={{ width: '13%' }}>Timestamp</th>
                                <th style={{ width: '32%' }}>Notes</th>
                                <th style={{ width: '10%' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {props.personnelHiringHistory && props.personnelHiringHistory.length > 0 ? (
                                <>
                                    {props.personnelHiringHistory?.map((history: PersonnelHiringHistory, index: number) => (
                                        <tr style={{ width: '100%' }} key={index}>
                                            <td style={{ width: '8%' }}>
                                                <RHFSelect
                                                    register={register}
                                                    name={`history.${index}.tranType`}
                                                    style={{ width: '100%', display: 'block' }}
                                                    options={[
                                                        { value: 'Hired', label: 'Hired' },
                                                        { value: 'Termed', label: 'Termed' }
                                                    ]}
                                                />
                                            </td>
                                            <td style={{ width: '7%' }}>
                                                <RHFInputBox
                                                    register={register}
                                                    name={`history.${index}.tranDate`}
                                                    type="date"
                                                    style={{ width: '100%', display: 'block' }}
                                                    defaultVal={history.tranDate?.toString()}
                                                />
                                            </td>
                                            <td style={{ width: '5%' }}>
                                                <RHFInputBox
                                                    register={register}
                                                    name={`history.${index}.hireByNo`}
                                                    type="text"
                                                    style={{ width: '100%', display: 'block' }}
                                                    defaultVal={history.hireByNo?.toString()}
                                                />
                                            </td>
                                            <td style={{ width: '10%' }}>
                                                <RHFInputBox
                                                    register={register}
                                                    name={`history.${index}.hireBy`}
                                                    type="text"
                                                    style={{ width: '100%', display: 'block' }}
                                                    defaultVal={history.hireBy}
                                                />
                                            </td>
                                            <td style={{ width: '5%' }}>
                                                <RHFInputBox
                                                    register={register}
                                                    name={`history.${index}.tranByNo`}
                                                    type="text"
                                                    style={{ width: '100%', display: 'block' }}
                                                    defaultVal={history.tranByNo?.toString()}
                                                />
                                            </td>
                                            <td style={{ width: '10%' }}>
                                                <RHFInputBox
                                                    register={register}
                                                    name={`history.${index}.tranBy`}
                                                    type="text"
                                                    style={{ width: '100%', display: 'block' }}
                                                    defaultVal={history.tranBy}
                                                />
                                            </td>
                                            <td style={{ width: '13%' }}>
                                                <RHFInputBox
                                                    register={register}
                                                    name={`history.${index}.timestamp`}
                                                    type="text"
                                                    style={{ width: '100%', display: 'block' }}
                                                    disabled={true}
                                                    defaultVal={history.timestamp?.toString()}
                                                />
                                            </td>
                                            <td style={{ width: '32%' }}>
                                                <RHFInputBox
                                                    register={register}
                                                    name={`history.${index}.notes`}
                                                    type="text"
                                                    style={{ width: '100%', display: 'block' }}
                                                    defaultVal={history.notes}
                                                />
                                            </td>
                                            <td style={{ width: '10%', verticalAlign: 'bottom', paddingLeft: '2%' }}>
                                                <div className="form-row" style={{marginLeft: '2%'}}>
                                                    <a onClick={() => handleUpdateRow(index)}>
                                                        <i style={{ paddingRight: '1%', fontSize: '2.5rem' }} className="far fa-save mr-1 clickable" />
                                                    </a>
                                                    <a onClick={() => handleDeleteRow(index)}>
                                                        <i style={{ fontSize: '2.5rem' }} className="far fa-trash mr-1 clickable" />
                                                    </a>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    <NewHistoryEntry />
                                </>) :

                                <NewHistoryEntry />}
                        </tbody>
                    </table>
                    <div className="form-row" style={{ marginTop: '2rem', marginLeft: '33.5%', textAlign: 'center' }}>
                        <Button
                            className="blue-hover-button"
                            style={{ width: '25%', marginRight: '1rem' }}
                            text={'Add'}
                            type="submit"
                            intent="primary"
                        />
                        <Button
                            className="blue-hover-button"
                            style={{ width: '25%', marginRight: '1rem' }}
                            text={'Close'}
                            onClick={() => props.hideDialog()}
                        />
                    </div>
                </form>
            </div>
        </Dialog >
    );
};

export default PersonnelHiringHistoryDialog;


// WEBPACK FOOTER //
// ./src/components/personnel/dialogs/PersonnelHiringHistoryDialog.tsx